// Typed fetchers for the admin dashboard. Every call attaches the user's
// JWT — the server's requireAdmin middleware looks up the email and 403s
// when it's not in ADMIN_EMAILS. If the JWT is missing or stale we get a
// 401 and surface that to the caller so the UI can redirect to sign-in.

import { getAuth } from '@/lib/auth/auth';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

async function get<T>(path: string): Promise<T> {
  const jwt = getAuth().current()?.jwt;
  if (!jwt) throw new Error('Sign in required');
  const res = await fetch(`${API_BASE}/admin${path}`, {
    headers: { authorization: `Bearer ${jwt}` },
    credentials: 'include',
  });
  if (res.status === 401) throw new Error('Sign in required');
  if (res.status === 403) throw new Error('Forbidden — your account is not on the admin allowlist');
  if (res.status === 503) throw new Error('Admin allowlist not configured on the server');
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return (await res.json()) as T;
}

// ---- Shapes (mirror server/src/admin/queries.ts) --------------------------

export interface SummaryPayload {
  cost7d: number;
  dau: number;
  signupsThisWeek: number;
  masteryRate: number;
  wordsPerActiveHour: number;
}

export interface UsageDailyRow {
  day: string;
  provider: 'anthropic' | 'deepseek';
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cost: number;
}

export interface UsageTopUserRow {
  userId: string;
  email: string | null;
  calls: number;
  cost: number;
}

export interface UsageEndpointRow {
  endpoint: string;
  calls: number;
  cost: number;
}

export interface PmfActiveRow {
  day: string;
  dau: number;
}

export interface PmfActivationRow {
  week: string;
  signups: number;
  activated: number;
  rate: number;
}

export interface PmfRetentionRow {
  cohortWeek: string;
  size: number;
  d1: number | null;
  d7: number | null;
  d30: number | null;
}

export interface PmfFunnelRow {
  stage: string;
  count: number;
  conversionFromPrev: number;
}

export interface PmfPersonaRow {
  personaId: string;
  matches: number;
  avgSessionMs: number;
  retention7d: number;
}

export interface PmfHabitResult {
  histogram: { sessionsInWeek: number; userWeekCount: number }[];
  medianSessionsPerActiveWeek: number;
}

export interface LearningCurvePoint {
  exposureN: number;
  attempts: number;
  accuracyPct: number;
}

export interface LearningLatencyPoint {
  exposureN: number;
  attempts: number;
  medianMs: number;
}

export interface ForgettingPoint {
  daysSinceLastSeenBucket: string;
  attempts: number;
  accuracyPct: number;
}

export interface LearningHeadline {
  wordsFirstRecognizedUnaided: number;
  activeHours: number;
  wordsPerActiveHour: number;
}

export interface SourceRetentionResult {
  chat: { firstEncounters: number; retention7dPct: number };
  flashcard: { firstEncounters: number; retention7dPct: number };
}

export interface FeedbackRow {
  ts: string;
  userId: string | null;
  email: string | null;
  likeRating: number;
  continueRating: number;
  message: string | null;
}

// ---- Endpoints ------------------------------------------------------------

export const adminApi = {
  summary: () => get<SummaryPayload>('/summary'),
  usageDaily: () => get<UsageDailyRow[]>('/usage/daily'),
  usageTopUsers: (limit = 10) => get<UsageTopUserRow[]>(`/usage/top-users?limit=${limit}`),
  usageEndpoints: () => get<UsageEndpointRow[]>('/usage/endpoints'),
  pmfActive: () => get<PmfActiveRow[]>('/pmf/active'),
  pmfActivation: (weeks = 12) => get<PmfActivationRow[]>(`/pmf/activation?weeks=${weeks}`),
  pmfRetention: (weeks = 12) => get<PmfRetentionRow[]>(`/pmf/retention?weeks=${weeks}`),
  pmfFunnel: () => get<PmfFunnelRow[]>('/pmf/funnel'),
  pmfPersonas: () => get<PmfPersonaRow[]>('/pmf/personas'),
  pmfHabit: (weeks = 8) => get<PmfHabitResult>(`/pmf/habit?weeks=${weeks}`),
  learningMasteryCurve: () => get<LearningCurvePoint[]>('/learning/mastery-curve'),
  learningLatencyCurve: () => get<LearningLatencyPoint[]>('/learning/latency-curve'),
  learningForgettingCurve: () => get<ForgettingPoint[]>('/learning/forgetting-curve'),
  learningHeadline: () => get<LearningHeadline>('/learning/headline'),
  learningSourceRetention: () => get<SourceRetentionResult>('/learning/source-retention'),
  feedbackRecent: (limit = 50) => get<FeedbackRow[]>(`/feedback/recent?limit=${limit}`),
};

// ---- Small util: which emails see the admin link? -------------------------

export function adminEmailAllowlist(): Set<string> {
  const raw = import.meta.env.VITE_ADMIN_EMAILS ?? '';
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0),
  );
}

export function isAdminEmail(email: string | undefined): boolean {
  if (!email) return false;
  return adminEmailAllowlist().has(email.toLowerCase());
}
